<template>
  <div class="row main-content">
    <div class="col">
      <h1 class="page-title font-custom text-uppercase mt-3">
        {{ $t('title.user.list') }}
      </h1>

      <div
        v-if="isProcessing"
        class="text-center"
      >
        <img
          src="@/assets/img/loader.gif"
          class="center-block lazyload"
        >
      </div>

      <div
        v-else-if="error"
        class="alert alert-warning text-center border-0 shadow-sm rounded-0"
      >
        {{ $t("error." + `${responseErr}`) }}
      </div>

      <div v-else-if="users && users.length">
        <!-- Table -->
        <div class="table-responsive">
          <table class="table table-hover table-sm table-striped border">
            <thead class="font-custom">
              <tr>
                <th>{{ $t('label.user.selectedIdentification') }}</th>
                <th>{{ $t('label.user.selectedEmail') }}</th>
                <th>{{ $t('label.user.selectedRole') }}</th>
                <th>{{ $t('label.user.statut') }}</th>
                <th>{{ $t('label.user.selectedInternal') }}</th>
                <th>{{ $t('label.user.selectedLanguage') }}</th>
                <th>{{ $t('label.user.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in users"
                :key="user.id"
                class="mb-3"
              >
                <td>{{ user.login }}</td>
                <td>{{ user.email }}</td>
                <td>{{ user.ihmRoleRef }}</td>
                <td>{{ user.userStatus }}</td>
                <td>{{ user.internal }}</td>
                <td>{{ user.language }}</td>
                <td class="pl-2">
                  <template v-if="hasPermission('users:write')">
                    <BaseButton
                      id="editUser"
                      type="button"
                      class="btn btn-sm btn-primary font-custom rounded-pill border me-2 mb-1 mt-1"
                      :title="$t('title.user.info.editUser') + ' \'' + user.login + '\'' "
                      @click-base-button-event-emitter="editUser(user)"
                    >
                      {{ $t('button.edit') }}
                    </BaseButton>
                    <template v-if="!isLoggedUserAccount(user)">
                      <template v-if="isAccountLockedStatus(user)">
                        <BaseButton
                          id="unlockUser"
                          type="button"
                          class="base-button font-custom rounded-pill btn-white btn-sm d-inline border me-2 mb-1 mt-1"
                          :title="$t('title.user.info.unlockUser') + ' \'' + user.login + '\'' "
                          @click-base-button-event-emitter="showConfirmation (user, unlockUser)"
                        >
                          {{ $t("button.unlock") }}
                        </BaseButton>
                      </template>
                      <template v-else>
                        <BaseButton
                          id="reinitPwd"
                          type="button"
                          class="base-button font-custom rounded-pill btn-white btn-sm d-inline border me-2 mb-1 mt-1"
                          :title="$t('title.user.info.reinitUserPwd') + ' \'' + user.login + '\'' "
                          @click-base-button-event-emitter="reinitPwd(user)"
                        >
                          {{ $t("button.reinitPwd") }}
                        </BaseButton>

                        <BaseButton
                          id="lockUser"
                          type="button"
                          class="base-button font-custom rounded-pill btn-white btn-sm d-inline border me-2 mb-1 mt-1"
                          :title="$t('title.user.info.lockUser') + ' \'' + user.login + '\'' "
                          @click-base-button-event-emitter="showConfirmation (user, lockUser)"
                        >
                          {{ $t("button.lock") }}
                        </BaseButton>
                      </template>
                      <template v-if="user.otpconfigured">
                        <BaseButton
                          id="resetOTP"
                          type="button"
                          class="base-button font-custom rounded-pill btn-secondary btn-sm d-inline border me-2 mb-1 mt-1"
                          :title="$t('title.user.info.resetOtp') + ' \'' + user.login + '\'' "
                          @click-base-button-event-emitter="showConfirmation (user, resetOTP)"
                        >
                          {{ $t("button.resetOtp") }}
                        </BaseButton>
                      </template>
                    </template>
                    <template v-if="!isLoggedUserAccount(user)">
                      <BaseButton
                        id="deleteUser"
                        type="button"
                        class="btn btn-sm btn-danger font-custom rounded-pill mb-1 mt-1"
                        :title="$t('title.user.info.deleteUser') + ' \'' + user.login + '\'' "
                        @click-base-button-event-emitter="showConfirmation (user, deleteUser)"
                      >
                        {{ $t("button.delete") }}
                      </BaseButton>
                    </template>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div
      id="successModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="successModalLabel"
      aria-hidden="true"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="successModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.SUCCESS_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-success">
              <p id="successModalMsg" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div
      id="errorModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="errorModalLabel"
      aria-hidden="true"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="errorModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.ERROR_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-danger">
              <p id="errorModalMsg" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div
      id="confirmationModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="confirmationModalLabel"
      aria-hidden="true"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="confirmationModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.CONFIRMATION_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              {{ $t('label.modal.CONFIRMATION_MODAL_MSG') }}
            </div>
          </div>
          <div class="modal-footer">
            <BaseButton
              type="button"
              data-bs-dismiss="modal"
              class="btn btn-primary"
              @click-base-button-event-emitter="executeConfirmedAction()"
            >
              {{ $t('label.modal.CONFIRMATION_MODAL_YES_BUTTON') }}
            </BaseButton>
            <button
              type="button"
              class="btn rounded-pill btn-secondary font-custom"
              data-bs-dismiss="modal"
            >
              {{ $t('label.modal.CONFIRMATION_MODAL_NO_BUTTON') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="./UserList.js"></script>

<style src="./UserList.scss" scoped lang="scss"></style>
